import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { TextInput, Button, CloudSelector, ToggleCloudControl } from 'procyon-ui' // Imported TextInput

// Core & Utils
import { ApiProvider } from 'Core'
import useAppView from 'Core/Hooks/useAppView'
import useMemoSelector from 'Core/Hooks/useMemoSelector'
import { serviceAccountsSlice } from 'infra/redux/reducers'
import { isOrgAccount, useCloudTypeRouteProvider, useCloudTypes } from 'features/clouds'
import { getResourceName } from 'features/resources'
import { getRequestPayload, getServiceAccountPayload } from './../utils'

// Components
import BrandSelect from 'Components/BrandSelect'
import ErrorBox from 'Components/ErrorBox/ErrorBox'
import ThemeDropDown from 'Components/ThemeDropDown'
import PasswordInput from 'Components/PasswordInput'
import { FullScreenModal } from 'V2Components'

const AddServiceAccount = ({ onClose, open }) => {
  const { viewMode } = useAppView()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.user)
  const [selectedCloudType, setSelectedCloudType] = useState('AWS')

  // API Providers
  const serviceAccountApiProviderInstance = useRef(new ApiProvider('serviceaccounts'))
  const approvalReqApiProviderInstance = useRef(new ApiProvider('approvalreqs'))

  // Selectors
  const { data: projects } = useMemoSelector('projects')
  const { data: accountList } = useMemoSelector('accountList')

  // Options Logic
  const projectOptions = useMemo(
    () =>
      projects.map((e) => ({
        label: e.Spec.DisplayName,
        value: e.ObjectMeta.ID
      })),
    [projects]
  )

  const getAccountOptions = useCallback(
    (accountType) => {
      return accountList
        .filter((e) => e.Spec.Type === accountType && !isOrgAccount(e))
        .map((e) => ({
          label: getResourceName(e),
          value: e.ObjectMeta.ID
        }))
    },
    [accountList]
  )

  // Form Logic
  const InitialValues = useMemo(
    () => ({
      Project: '',
      Account: '',
      accountType: 'AWS',
      Description: '',
      EncKey: '',
      Name: '',
      UserName: '',
      DisplayName: '',
      viewMode,
      GivenName: '',
      Comment: ''
    }),
    [viewMode]
  )

  const VALIDATION_RULES = useMemo(
    () =>
      Yup.object({
        EncKey: Yup.string()
          .trim('Credentials Passphrase cannot include leading and trailing spaces')
          .strict(false)
          .required('*Credentials Passphrase is required'),
        Project: Yup.string().when('accountType', {
          is: 'GCP',
          then: Yup.string().required('*Project is required.')
        }),
        Account: Yup.string().when('accountType', {
          is: (value) => ['AWS', 'AZURE'].includes(value),
          then: Yup.string().required('*Account is required.')
        }),
        Name: Yup.string().when('accountType', {
          is: 'GCP',
          then: Yup.string().required('*Name is required.')
        }),
        DisplayName: Yup.string().when('accountType', {
          is: 'AZURE',
          then: Yup.string().required('*Display Name is required.')
        }),
        GivenName: Yup.string().when('viewMode', {
          is: 'user',
          then: Yup.string()
            .required('*Request Name is required.')
            .min(2, 'Request name is too small.')
        }),
        UserName: Yup.string().when('accountType', {
          is: 'AWS',
          then: Yup.string()
            .trim('UserName cannot include leading and trailing spaces')
            .strict(false)
            .test('nospaces', 'Spaces are not allowed.', (value) => !/\s/g.test(value))
            .required('*UserName is required')
        })
      }),
    []
  )

  const onSubmit = useCallback(
    async (fields) => {
      formik.setSubmitting(true)
      try {
        let formPayload
        if (viewMode === 'admin') {
          formPayload = getServiceAccountPayload(fields)
          await serviceAccountApiProviderInstance.current.setInstance(formPayload).post()
        } else if (viewMode === 'user') {
          formPayload = getRequestPayload(fields, user)
          await approvalReqApiProviderInstance.current.setInstance(formPayload).post()
        }
        await dispatch(serviceAccountsSlice.thunk())
        onClose()
      } catch (error) {
        console.error('Error submitting form', error)
      } finally {
        formik.setSubmitting(false)
        formik.resetForm(InitialValues)
      }
    },
    [onClose, viewMode, user, dispatch, InitialValues]
  )

  const formik = useFormik({
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit,
    initialValues: InitialValues,
    validationSchema: VALIDATION_RULES
  })

  // Handlers
  const handleProjectChange = (e) => formik.setFieldValue('Project', e.target.value)
  const handleAccountChange = (e) => formik.setFieldValue('Account', e.target.value)
  const setAccountType = (type) => formik.setFieldValue('accountType', type)

  // Layout Helpers
  const actionLabel = viewMode === 'admin' ? 'Add Service Account' : 'Request Service Account'

  return (
    <FullScreenModal showModal={open}>
      <div className='flex justify-center items-center h-[100%] bg-[#2229455a]'>
        <div className='w-[626px] rounded-lg p-8 bg-white'>
          <form className='flex flex-col h-full bg-white' onSubmit={formik.handleSubmit} noValidate>
            {/* Brand Selection Section */}
            <div className='flex flex-col'>
              <ToggleCloudControl
                ariaLabel='cloud-select'
                onChange={(_, v) => v && setAccountType(v)}
                options={['AWS', 'GCP', 'AZURE']}
                value={formik.values.accountType}
              />
            </div>

            {/* Form Inputs Container */}
            <div className='flex-grow flex flex-col justify-center py-4 space-y-2'>
              {/* USER VIEW FIELDS */}
              {viewMode === 'user' && (
                <>
                  <div className='w-full mb-3'>
                    <TextInput
                      {...formik.getFieldProps('GivenName')}
                      label='Request Name'
                      placeholder='Enter Request Name'
                      error={!!(formik.touched.GivenName && formik.errors.GivenName)}
                      sx={{ width: '100%' }}
                    />
                    <ErrorBox error={formik.errors.GivenName} touched={formik.touched.GivenName} />
                  </div>
                  <div className='w-full mb-3'>
                    <TextInput
                      {...formik.getFieldProps('Comment')}
                      label='Comment'
                      placeholder='Enter Comment'
                      error={!!(formik.touched.Comment && formik.errors.Comment)}
                      sx={{ width: '100%' }}
                    />
                    <ErrorBox error={formik.errors.Comment} touched={formik.touched.Comment} />
                  </div>
                </>
              )}

              {/* GCP SPECIFIC */}
              {formik.values.accountType === 'GCP' && (
                <>
                  <div className='w-full mb-3'>
                    <TextInput
                      {...formik.getFieldProps('Name')}
                      label='Name'
                      placeholder='Enter Name'
                      error={!!(formik.touched.Name && formik.errors.Name)}
                      sx={{ width: '100%' }}
                    />
                    <ErrorBox error={formik.errors.Name} touched={formik.touched.Name} />
                  </div>
                  <div className='w-full mb-3'>
                    <TextInput
                      {...formik.getFieldProps('Description')}
                      label='Description'
                      placeholder='Enter Description'
                      error={!!(formik.touched.Description && formik.errors.Description)}
                      sx={{ width: '100%' }}
                    />
                    <ErrorBox
                      error={formik.errors.Description}
                      touched={formik.touched.Description}
                    />
                  </div>
                  <div className='w-full mb-3'>
                    <ThemeDropDown
                      label='Project'
                      options={projectOptions}
                      labelExtractor={(e) => e.label}
                      valueExtractor={(e) => e.value}
                      value={formik.values.Project}
                      onChange={handleProjectChange}
                      onClose={() => setTimeout(() => formik.setFieldTouched('Project'), 100)}
                      errored={formik.errors.Project}
                      touched={formik.touched.Project}
                    />
                  </div>
                </>
              )}

              {/* AZURE SPECIFIC */}
              {formik.values.accountType === 'AZURE' && (
                <div className='w-full mb-3'>
                  <TextInput
                    {...formik.getFieldProps('DisplayName')}
                    label='Display Name'
                    placeholder='Enter Display Name'
                    error={!!(formik.touched.DisplayName && formik.errors.DisplayName)}
                    sx={{ width: '100%' }}
                  />
                  <ErrorBox
                    error={formik.errors.DisplayName}
                    touched={formik.touched.DisplayName}
                  />
                </div>
              )}

              {/* AWS SPECIFIC */}
              {formik.values.accountType === 'AWS' && (
                <div className='w-full mb-3'>
                  <TextInput
                    {...formik.getFieldProps('UserName')}
                    label='User Name'
                    placeholder='Enter User Name'
                    error={!!(formik.touched.UserName && formik.errors.UserName)}
                    sx={{ width: '100%' }}
                  />
                  <ErrorBox error={formik.errors.UserName} touched={formik.touched.UserName} />
                </div>
              )}

              {/* AWS & AZURE DROPDOWN */}
              {['AWS', 'AZURE'].includes(formik.values.accountType) && (
                <div className='w-full mb-3'>
                  <ThemeDropDown
                    label='Account'
                    options={getAccountOptions(formik.values.accountType)}
                    labelExtractor={(e) => e.label}
                    valueExtractor={(e) => e.value}
                    value={formik.values.Account}
                    onChange={handleAccountChange}
                    onClose={() => setTimeout(() => formik.setFieldTouched('Account'), 100)}
                    errored={formik.errors.Account}
                    touched={formik.touched.Account}
                  />
                </div>
              )}

              {/* PASSWORD FIELD (ALL) */}
              <div className='w-full mb-3'>
                {/* Keeping PasswordInput but wrapped in Tailwind structure */}
                <PasswordInput
                  size='small'
                  {...formik.getFieldProps('EncKey')}
                  id='EncKey'
                  label='Credentials Passphrase'
                  color='primary'
                  errored={formik.errors.EncKey}
                  touched={formik.touched.EncKey}
                  fullWidth
                />
                <ErrorBox error={formik.errors.EncKey} touched={formik.touched.EncKey} />
              </div>
            </div>

            {/* Footer Actions */}
            <div className='flex justify-end mt-4 items-center gap-3'>
              <Button variant='gray' disabled={formik.isSubmitting} onClick={onClose}>
                Cancel
              </Button>

              <Button
                variant='primary'
                type='submit'
                onClick={formik.handleSubmit}
                disabled={!formik.isValid || formik.isSubmitting}
              >
                {actionLabel}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </FullScreenModal>
  )
}

export default AddServiceAccount
